import { Expose } from 'class-transformer';
import { Point } from 'geojson';

import { FsAxonDeviceAttributes } from '@lib/api/entities';
import { IDevice } from '@lib/axon/interfaces/device.interface';
import { PresenceDeviceModels } from '@lib/axon/models/presence-device-model.enum';
import { DEVICE_ALERT_TYPES } from '@lib/axon/services/alerts.constants';
import { SnModel } from '@lib/common/models';
import { t } from '@lib/translation';

export class FsPresenceDevice extends SnModel implements IDevice {
  @Expose()
  public type: string;

  @Expose()
  public geometry: Point;

  @Expose()
  public properties: {
    id: string;
    updatedAt: string;
    entityType: string;
    model?: string;
    serial?: string;
    name?: string;
    active?: boolean;
    bwc?: {
      docked?: boolean;
    };
    loc?: {
      cUnc?: number;
      updatedAt: string;
    };
    venue?: {
      id: string;
      name: string;
      provider: string;
    };
    floor?: {
      id: string;
      name: string;
      level: number;
      provider: string;
    };
    motion?: {
      speed?: number;
      track?: number;
    };
    connectivity?: {
      cellular?: [
        {
          id: string;
          strength: number;
        },
      ];
      wifi?: [
        {
          id: string;
          strength: number;
        },
      ];
    };
    cameras?: {
      status: string;
      cams?: [
        {
          id: string;
          model: string;
          status: string;
          streamable?: boolean;
          unStreamableReason?: string;
          cameraId?: string;
          peripheral?: string;
        },
      ];
    };
    rto?: {
      watchMe: boolean;
      watchMeAt: string;
      panic: boolean;
      panicAt: string;
    };
    users: [
      {
        id: string;
        firstName: string;
        lastName: string;
        badgeNumber?: string;
        email?: string;
        internalId?: string;
      },
    ];
    cxId?: string;
  };

  get attributes(): FsAxonDeviceAttributes {
    const attrs = new FsAxonDeviceAttributes();
    attrs.assignees = this.properties.users;
    attrs.batteries = [];
    attrs.deviceSerial = this.properties.serial;
    attrs.location = this.geometry;
    attrs.signalStrengths = [];
    attrs.rawStatus = this.properties.cameras?.status;
    attrs.status = this.properties.cameras?.status;
    attrs.stealth = { state: this.properties.cameras?.status };
    attrs.stream = [];
    attrs.axonDeviceId = this.properties.id;
    attrs.deviceModel = this.properties.model;
    attrs.deviceUpdateTimestamp = new Date(this.properties.updatedAt).getTime();
    attrs.partnerId = this.properties.id;
    attrs.partnerName = this.properties.name;
    attrs.correlation = {
      id: this.properties.id,
      timestamp: new Date(this.properties.loc?.updatedAt).getTime(),
    };
    attrs.vehicle = [];
    return attrs;
  }

  get deviceName(): string {
    switch (this.properties.model) {
      case PresenceDeviceModels.AB3:
      case PresenceDeviceModels.AB4:
      case PresenceDeviceModels.ABW:
      case PresenceDeviceModels.ABW_MINI:
        if (this.properties.users && this.properties.users.length > 0) {
          return `${this.properties.users[0]?.firstName} ${this.properties.users[0]?.lastName}`;
        }
        return 'Unassigned';
      case PresenceDeviceModels.Fleet3:
      case PresenceDeviceModels.Outpost:
      case PresenceDeviceModels.AxonOutpost:
        return this.properties.name || t('common.unassigned');
      default:
        return 'Unassigned';
    }
  }

  get deviceCategory(): string {
    switch (this.properties.model) {
      case PresenceDeviceModels.AB3:
      case PresenceDeviceModels.AB4:
      case PresenceDeviceModels.ABW:
      case PresenceDeviceModels.ABW_MINI:
        return 'BWC';
      case PresenceDeviceModels.Fleet3:
        return 'Fleet Camera';
      case PresenceDeviceModels.Outpost:
      case PresenceDeviceModels.AxonOutpost:
        return t('cameras.fixedCamera');
      default:
        return 'N/A';
    }
  }

  get primaryAssignee(): any {
    if (!this.properties?.users?.length) {
      return null;
    }
    return this.properties.users[0];
  }

  get axonDeviceId(): string {
    if (!this.properties?.id) return '';

    const match = this.properties.id.match(/urn:axon:device:id:(.+)$/);
    return match ? match[1] : this.properties.id;
  }

  get deviceModel(): string {
    return this.properties.model;
  }

  get deviceStatus(): string {
    return this.properties.cameras.status;
  }

  get lastUpdated(): string {
    return this.properties.updatedAt;
  }

  get deviceModelInternal(): string {
    return this.properties.model;
  }

  get deviceUpdateTimestamp(): number {
    return new Date(this.properties.updatedAt).getTime();
  }

  get partnerId(): string {
    return this.properties.id;
  }

  get partnerName(): string {
    return this.properties.name;
  }

  get alertTypes(): string[] {
    const alertTypes: string[] = [];
    if (this.properties?.rto?.panic) {
      alertTypes.push(DEVICE_ALERT_TYPES.PANIC);
    }
    if (this.properties?.rto?.watchMe) {
      alertTypes.push(DEVICE_ALERT_TYPES.WATCH_ME);
    }
    return alertTypes;
  }

  get filteredAnaltyicData(): any {
    const p = this.properties || ({} as any);
    const sanitizedDevice = {
      id: p.id,
      updatedAt: p.updatedAt,
      entityType: p.entityType,
      model: p.model,
      serial: p.serial,
      active: p.active,
      bwc: p.bwc,
      connectivity: p.connectivity,
      cameras: p.cameras,
      rto: p.rto,
      cxId: p.cxId,
      userId: (p.users ?? [])[0]?.id,
      // intentionally omit: full users, name, loc
    };

    return {
      device: sanitizedDevice,
    };
  }

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}
